import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { useWebSocket } from "../hooks/useWebSocket";
import { usePushNotifications } from "../hooks/usePushNotifications";
import SignaturePad from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Package,
  MapPin,
  Navigation,
  Phone,
  Clock,
  CheckCircle2,
  Truck,
  Camera,
  Send,
  MessageCircle,
  User,
  RefreshCw,
  Wifi,
  WifiOff,
  Home,
  FileText,
  Loader2,
  LogOut,
  ArrowLeft,
  X,
  Eye,
  Info,
  Bell,
  BellOff,
  AlertCircle,
  Image,
  PenLine,
  MoreVertical,
  Trash2,
  Copy,
  RotateCcw,
  Building2,
  ChevronDown,
  ChevronRight
} from "lucide-react";

export default function AppEntregasPage() {
  // Tab state: 'entregas' or 'chat'
  const [activeTab, setActiveTab] = useState('entregas');
  
  // Entregas state
  const [entregas, setEntregas] = useState({ pendientes: [], en_proceso: [], completadas: [], devueltas: [] });
  const [loading, setLoading] = useState(true);
  const [selectedEntrega, setSelectedEntrega] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDevueltoDialog, setShowDevueltoDialog] = useState(false);
  const [devueltoLoading, setDevueltoLoading] = useState(false);
  const [motivoDevolucion, setMotivoDevolucion] = useState("");
  const [entregasView, setEntregasView] = useState('en_proceso'); // 'pendientes', 'en_proceso', 'completadas', 'devueltas'
  const [confirmData, setConfirmData] = useState({
    recipient_name: "",
    notes: "",
    photo_data: null
  });
  const [confirming, setConfirming] = useState(false);
  
  // Chat state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  
  const signatureRef = useRef(null);
  const photoInputRef = useRef(null);
  const chatEndRef = useRef(null);
  
  const { user, token, getAuthHeaders, logout } = useAuth();
  const { isConnected, onMessage, joinRoom, leaveRoom } = useWebSocket(user?.id);
  const { isSupported: pushSupported, permission: pushPermission, isSubscribed: pushSubscribed, loading: pushLoading, error: pushError, subscribe: subscribePush } = usePushNotifications(token);

  // Show notification prompt on first load if not subscribed
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  useEffect(() => {
    if (pushSupported && pushPermission === 'default' && !pushSubscribed) {
      // Show prompt after a short delay
      const timer = setTimeout(() => setShowNotificationPrompt(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [pushSupported, pushPermission, pushSubscribed]);

  // Log push status for debugging
  useEffect(() => {
    console.log('[AppEntregas] Push status:', { 
      pushSupported, 
      pushPermission, 
      pushSubscribed, 
      pushLoading,
      pushError 
    });
  }, [pushSupported, pushPermission, pushSubscribed, pushLoading, pushError]);

  const handleEnableNotifications = async () => {
    if (pushPermission === 'denied') {
      toast.error("Las notificaciones están bloqueadas. Habilítalas en la configuración del navegador.");
      return;
    }
    
    toast.info("Solicitando permisos de notificación...");
    const success = await subscribePush();
    if (success) {
      toast.success("¡Notificaciones activadas! Haz clic en la campana para probar.");
    } else {
      if (pushPermission === 'denied') {
        toast.error("Permiso denegado. Habilita las notificaciones en la configuración del navegador.");
      } else if (pushError) {
        toast.error(`Error: ${pushError}`);
      } else {
        toast.error("No se pudieron activar las notificaciones. Intenta de nuevo.");
      }
    }
    setShowNotificationPrompt(false);
  };

  const testPushNotification = async () => {
    try {
      toast.info("Enviando notificación de prueba...");
      const res = await axios.post(`${API}/push/test`, {}, getAuthHeaders());
      if (res.data.success) {
        toast.success("Notificación enviada. ¡Revisa tu dispositivo!");
      } else {
        toast.error(res.data.message || "Error al enviar notificación");
      }
    } catch (err) {
      console.error("Error testing push:", err);
      toast.error(err.response?.data?.detail || "Error al probar notificaciones");
    }
  };

  const fetchEntregas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/app/mis-entregas`, getAuthHeaders());
      setEntregas(res.data);
    } catch (err) {
      console.error("Error fetching entregas:", err);
      toast.error("Error al cargar entregas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoadingChat(true);
      const res = await axios.get(`${API}/mensajeria/solicitudes`, getAuthHeaders());
      const solicitudesWithChat = res.data.filter(s => 
        s.user_id === user?.id || s.mensajero_id === user?.id
      );
      setConversations(solicitudesWithChat);
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setLoadingChat(false);
    }
  }, [getAuthHeaders, user?.id]);

  useEffect(() => {
    fetchEntregas();
    fetchConversations();
  }, [fetchEntregas, fetchConversations]);

  // WebSocket handlers
  useEffect(() => {
    const unsubDelivery = onMessage("delivery_confirmed", () => {
      fetchEntregas();
      toast.success("Entrega confirmada");
    });

    const unsubTaken = onMessage("delivery_taken", () => {
      fetchEntregas();
    });

    const unsubNewMessage = onMessage("new_message", (data) => {
      if (selectedConversation && data.message.solicitud_id === selectedConversation.id) {
        setChatMessages(prev => [...prev, data.message]);
      } else {
        toast.info(`Nuevo mensaje de ${data.message.sender_name}`);
      }
    });

    return () => {
      unsubDelivery();
      unsubTaken();
      unsubNewMessage();
    };
  }, [onMessage, fetchEntregas, selectedConversation]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const tomarEntrega = async (solicitudId) => {
    try {
      await axios.post(`${API}/app/tomar-entrega/${solicitudId}`, {}, getAuthHeaders());
      toast.success("Entrega asignada");
      fetchEntregas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al tomar entrega");
    }
  };

  const openConfirmDialog = (entrega) => {
    setSelectedEntrega(entrega);
    setConfirmData({
      recipient_name: entrega.destino_nombre || "",
      notes: "",
      photo_data: null
    });
    setShowConfirmDialog(true);
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfirmData(prev => ({ ...prev, photo_data: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const confirmDelivery = async () => {
    if (!confirmData.recipient_name.trim()) {
      toast.error("Ingrese el nombre de quien recibe");
      return;
    }

    if (signatureRef.current?.isEmpty()) {
      toast.error("Se requiere la firma del destinatario");
      return;
    }

    setConfirming(true);
    try {
      let latitude = null, longitude = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        } catch (e) {
          console.log("Could not get location:", e);
        }
      }

      const signatureData = signatureRef.current.toDataURL();

      await axios.post(`${API}/entregas/confirmar`, {
        solicitud_id: selectedEntrega.id,
        recipient_name: confirmData.recipient_name,
        signature_data: signatureData,
        notes: confirmData.notes,
        photo_data: confirmData.photo_data,
        latitude,
        longitude
      }, getAuthHeaders());

      toast.success("¡Entrega confirmada!");
      setShowConfirmDialog(false);
      setSelectedEntrega(null);
      fetchEntregas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al confirmar entrega");
    } finally {
      setConfirming(false);
    }
  };

  const openDetailsDialog = async (entrega) => {
    try {
      // Load full details including delivery confirmation
      const res = await axios.get(`${API}/mensajeria/solicitudes/${entrega.id}`, getAuthHeaders());
      setSelectedEntrega(res.data);
      setShowDetailsDialog(true);
    } catch (err) {
      // Fallback to basic data if detailed fetch fails
      setSelectedEntrega(entrega);
      setShowDetailsDialog(true);
    }
  };

  const openDevueltoDialog = (entrega) => {
    setSelectedEntrega(entrega);
    setMotivoDevolucion("");
    setShowDevueltoDialog(true);
  };

  const marcarDevuelto = async () => {
    if (!selectedEntrega) return;
    if (!motivoDevolucion.trim()) {
      toast.error("Debe indicar el motivo de la devolución");
      return;
    }
    setDevueltoLoading(true);
    try {
      await axios.put(
        `${API}/mensajeria/solicitudes/${selectedEntrega.id}/estado?estado=devuelto&motivo_devolucion=${encodeURIComponent(motivoDevolucion.trim())}`,
        {},
        getAuthHeaders()
      );
      toast.success("Entrega marcada como devuelta");
      setShowDevueltoDialog(false);
      setSelectedEntrega(null);
      setMotivoDevolucion("");
      fetchEntregas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar como devuelta");
    } finally {
      setDevueltoLoading(false);
    }
  };

  const openChat = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const res = await axios.get(`${API}/chat/messages/${conversation.id}`, getAuthHeaders());
      setChatMessages(res.data);
    } catch (err) {
      console.error("Error loading chat:", err);
    }
    joinRoom(`chat_${conversation.id}`);
  };

  const closeChat = () => {
    if (selectedConversation) {
      leaveRoom(`chat_${selectedConversation.id}`);
    }
    setSelectedConversation(null);
    setChatMessages([]);
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const receiverId = selectedConversation.user_id === user?.id 
        ? selectedConversation.mensajero_id 
        : selectedConversation.user_id;
      
      await axios.post(`${API}/chat/messages`, {
        content: newMessage,
        solicitud_id: selectedConversation.id,
        receiver_id: receiverId
      }, getAuthHeaders());
      
      setNewMessage("");
    } catch (err) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingMessage(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`, getAuthHeaders());
      setChatMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success("Mensaje eliminado");
    } catch (err) {
      console.error("Error deleting message:", err);
      if (err.response?.status === 403) {
        toast.error("Solo puedes eliminar tus propios mensajes");
      } else {
        toast.error("Error al eliminar mensaje");
      }
    }
  };

  // Copy message
  const handleCopyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success("Mensaje copiado");
  };

  const openGPS = (entrega) => {
    // Use destino_direccion first, then direccion_envio as fallback
    const address = entrega.destino_direccion || entrega.direccion_envio;
    if (address && address.trim()) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    } else {
      toast.error("No hay dirección disponible");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  };

  // Get display address for card
  const getDisplayAddress = (entrega) => {
    return entrega.destino_direccion || entrega.direccion_envio || "Sin dirección";
  };

  const currentEntregas = entregasView === 'pendientes' ? entregas.pendientes :
                          entregasView === 'en_proceso' ? entregas.en_proceso :
                          entregasView === 'devueltas' ? entregas.devueltas :
                          entregas.completadas;

  // Group sucursal entregas
  const [expandedKioskoSucursales, setExpandedKioskoSucursales] = useState({});

  const { sucursalGroupsKiosko, individualEntregas } = (() => {
    if (!currentEntregas) return { sucursalGroupsKiosko: [], individualEntregas: [] };
    const groups = {};
    const individual = [];
    currentEntregas.forEach(ent => {
      if (ent.envio_sucursal && ent.sucursal) {
        if (!groups[ent.sucursal]) groups[ent.sucursal] = [];
        groups[ent.sucursal].push(ent);
      } else {
        individual.push(ent);
      }
    });
    return {
      sucursalGroupsKiosko: Object.entries(groups).map(([sucursal, items]) => ({ sucursal, items, count: items.length })),
      individualEntregas: individual
    };
  })();

  const toggleKioskoSucursal = (sucursal) => {
    setExpandedKioskoSucursales(prev => ({ ...prev, [sucursal]: !prev[sucursal] }));
  };

  // Render Chat Conversation View
  if (selectedConversation) {
    return (
      <div className="h-full bg-slate-100 flex flex-col safe-area-left safe-area-right">
        {/* Chat Header */}
        <div className="bg-[#0C314C] text-white px-4 py-3 safe-area-top">
          <div className="flex items-center gap-3">
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-white hover:bg-white/10 p-2 -ml-2"
              onClick={closeChat}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Avatar className="w-10 h-10 bg-[#45E69A]">
              <AvatarFallback className="bg-[#45E69A] text-[#0C314C] font-bold">
                {getInitials(selectedConversation.destino_nombre || selectedConversation.cliente)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold truncate">
                {selectedConversation.destino_nombre || selectedConversation.cliente || "Sin nombre"}
              </h1>
              <p className="text-xs text-slate-300 truncate">
                {selectedConversation.tipo_documento || "Entrega"}
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-3">
            {chatMessages.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No hay mensajes</p>
                <p className="text-xs mt-1">¡Inicia la conversación!</p>
              </div>
            ) : (
              chatMessages.map((msg) => {
                const isOwn = msg.sender_id === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
                  >
                    <div
                      className={`relative max-w-[80%] rounded-2xl px-4 py-2 ${
                        isOwn
                          ? 'bg-[#0C314C] text-white rounded-br-md'
                          : 'bg-white text-slate-900 shadow-sm rounded-bl-md'
                      }`}
                    >
                      {!isOwn && (
                        <p className="text-xs font-medium mb-1 text-slate-500">
                          {msg.sender_name}
                        </p>
                      )}
                      <p className="text-sm pr-6">{msg.content}</p>
                      <p className={`text-xs mt-1 ${isOwn ? 'text-slate-300' : 'text-slate-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('es-DO', { 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </p>
                      
                      {/* Message actions menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity ${
                              isOwn ? 'hover:bg-[#2a4d7a] text-white' : 'hover:bg-slate-100 text-slate-500'
                            }`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align={isOwn ? "end" : "start"}>
                          <DropdownMenuItem onClick={() => handleCopyMessage(msg.content)}>
                            <Copy className="w-4 h-4 mr-2" /> Copiar
                          </DropdownMenuItem>
                          {isOwn && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>

        {/* Message Input */}
        <div className="bg-white border-t border-slate-200 p-3 safe-area-bottom">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Escribe un mensaje..."
              className="flex-1"
              onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
            />
            <Button 
              onClick={sendChatMessage}
              disabled={sendingMessage || !newMessage.trim()}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] px-4 text-white"
            >
              {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-100 flex flex-col safe-area-left safe-area-right">
      {/* Header */}
      <div className="bg-[#0C314C] text-white px-4 py-3 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#45E69A] flex items-center justify-center">
              <User className="w-5 h-5 text-[#0C314C]" />
            </div>
            <div>
              <h1 className="font-semibold text-sm">{user?.name || "Mensajero"}</h1>
              <div className="flex items-center gap-1 text-xs text-slate-300">
                {isConnected ? (
                  <><Wifi className="w-3 h-3 text-green-400" /><span>Conectado</span></>
                ) : (
                  <><WifiOff className="w-3 h-3 text-red-400" /><span>Sin conexión</span></>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            {/* Notification Button */}
            {pushSupported && (
              <Button 
                size="sm" 
                variant="ghost" 
                className={`text-white hover:bg-white/10 h-9 w-9 p-0 ${pushSubscribed ? 'text-green-400' : ''}`}
                onClick={pushSubscribed ? testPushNotification : handleEnableNotifications}
                disabled={pushLoading}
                title={pushSubscribed ? "Probar notificación" : "Activar notificaciones"}
              >
                {pushLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : pushSubscribed ? (
                  <Bell className="w-4 h-4" />
                ) : (
                  <BellOff className="w-4 h-4" />
                )}
              </Button>
            )}
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-white hover:bg-white/10 h-9 w-9 p-0"
              onClick={() => { fetchEntregas(); fetchConversations(); }}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-white hover:bg-white/10 h-9 w-9 p-0"
              onClick={logout}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Notification Permission Prompt */}
      {showNotificationPrompt && (
        <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            <span className="text-sm">¿Activar notificaciones de entregas?</span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20 h-8 px-3 text-xs"
              onClick={() => setShowNotificationPrompt(false)}
            >
              Ahora no
            </Button>
            <Button
              size="sm"
              className="bg-white text-blue-600 hover:bg-blue-50 h-8 px-3 text-xs"
              onClick={handleEnableNotifications}
              disabled={pushLoading}
            >
              {pushLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Activar"}
            </Button>
          </div>
        </div>
      )}

      {/* Push Status Banner (shown when not subscribed) */}
      {pushSupported && !pushSubscribed && !showNotificationPrompt && (
        <div className={`px-4 py-2 flex items-center justify-between text-sm ${
          pushPermission === 'denied' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
        }`}>
          <div className="flex items-center gap-2">
            {pushPermission === 'denied' ? (
              <>
                <AlertCircle className="w-4 h-4" />
                <span>Notificaciones bloqueadas. Habilítalas en la configuración del navegador.</span>
              </>
            ) : (
              <>
                <BellOff className="w-4 h-4" />
                <span>Las notificaciones no están activas</span>
              </>
            )}
          </div>
          {pushPermission !== 'denied' && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs font-medium hover:bg-amber-200"
              onClick={handleEnableNotifications}
              disabled={pushLoading}
            >
              Activar ahora
            </Button>
          )}
        </div>
      )}

      {/* Push Error Banner */}
      {pushError && (
        <div className="bg-red-100 text-red-700 px-4 py-2 flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>Error: {pushError}</span>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-2 p-3 bg-white border-b">
        <div 
          className={`text-center p-2 rounded-lg cursor-pointer transition-colors ${
            entregasView === 'pendientes' && activeTab === 'entregas' ? 'bg-yellow-100' : 'bg-slate-50'
          }`}
          onClick={() => { setActiveTab('entregas'); setEntregasView('pendientes'); }}
        >
          <p className="text-xl font-bold text-yellow-600">{entregas.total_pendientes || 0}</p>
          <p className="text-xs text-slate-600">Pendientes</p>
        </div>
        <div 
          className={`text-center p-2 rounded-lg cursor-pointer transition-colors ${
            entregasView === 'en_proceso' && activeTab === 'entregas' ? 'bg-blue-100' : 'bg-slate-50'
          }`}
          onClick={() => { setActiveTab('entregas'); setEntregasView('en_proceso'); }}
        >
          <p className="text-xl font-bold text-blue-600">{entregas.total_en_proceso || 0}</p>
          <p className="text-xs text-slate-600">En Proceso</p>
        </div>
        <div 
          className={`text-center p-2 rounded-lg cursor-pointer transition-colors ${
            entregasView === 'completadas' && activeTab === 'entregas' ? 'bg-green-100' : 'bg-slate-50'
          }`}
          onClick={() => { setActiveTab('entregas'); setEntregasView('completadas'); }}
        >
          <p className="text-xl font-bold text-green-600">{entregas.completadas?.length || 0}</p>
          <p className="text-xs text-slate-600">Completadas</p>
        </div>
        <div 
          data-testid="tab-devueltas"
          className={`text-center p-2 rounded-lg cursor-pointer transition-colors ${
            entregasView === 'devueltas' && activeTab === 'entregas' ? 'bg-orange-100' : 'bg-slate-50'
          }`}
          onClick={() => { setActiveTab('entregas'); setEntregasView('devueltas'); }}
        >
          <p className="text-xl font-bold text-orange-600">{entregas.total_devueltas || 0}</p>
          <p className="text-xs text-slate-600">Devueltas</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'entregas' ? (
          <div className="p-3">
            {/* View Title */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">
                {entregasView === 'pendientes' ? 'Entregas Pendientes' :
                 entregasView === 'en_proceso' ? 'En Proceso' : 
                 entregasView === 'devueltas' ? 'Devueltas' : 'Completadas'}
              </h2>
              <Badge variant="outline">{currentEntregas?.length || 0}</Badge>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
              </div>
            ) : currentEntregas?.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No hay entregas {entregasView === 'pendientes' ? 'pendientes' : entregasView === 'en_proceso' ? 'en proceso' : entregasView === 'devueltas' ? 'devueltas' : 'completadas'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Sucursal Groups */}
                {sucursalGroupsKiosko.map(grupo => (
                  <Card key={`grupo-${grupo.sucursal}`} className="border-purple-200 shadow-sm overflow-hidden" data-testid={`kiosko-grupo-${grupo.sucursal}`}>
                    <div
                      className="flex items-center justify-between p-3 bg-purple-50 cursor-pointer active:bg-purple-100 transition-colors"
                      onClick={() => toggleKioskoSucursal(grupo.sucursal)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100">
                          <Building2 className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            {expandedKioskoSucursales[grupo.sucursal] ? (
                              <ChevronDown className="w-4 h-4 text-purple-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-purple-600" />
                            )}
                            <p className="font-semibold text-purple-900 text-sm">{grupo.sucursal}</p>
                          </div>
                          <p className="text-xs text-purple-600 ml-6">{grupo.count} {grupo.count === 1 ? 'envío' : 'envíos'}</p>
                        </div>
                      </div>
                      <Badge className="bg-purple-200 text-purple-800 border-0">{grupo.count}</Badge>
                    </div>
                    {expandedKioskoSucursales[grupo.sucursal] && (
                      <div className="border-t border-purple-200">
                        {grupo.items.map(entrega => (
                          <div key={entrega.id} className="p-3 border-b border-purple-100 last:border-0">
                            <div className="flex items-start justify-between mb-1">
                              <div className="flex-1">
                                <h3 className="font-medium text-slate-900 text-sm">{entrega.destino_nombre || entrega.cliente || "Sin nombre"}</h3>
                                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{getDisplayAddress(entrega)}</span>
                                </p>
                                {entrega.created_by_name && (
                                  <p className="text-xs text-purple-600 mt-0.5 flex items-center gap-1">
                                    <User className="w-3 h-3" />Solicitado por: <strong>{entrega.created_by_name}</strong>
                                  </p>
                                )}
                              </div>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500" onClick={() => openDetailsDialog(entrega)}>
                                <Eye className="w-3 h-3 mr-1" />Detalles
                              </Button>
                            </div>
                            <div className="flex gap-2 mt-2">
                              {entregasView === "pendientes" && (
                                <Button size="sm" className="flex-1 bg-[#0C314C] hover:bg-[#2a4d7a] text-white h-8 text-xs" onClick={() => tomarEntrega(entrega.id)}>
                                  <Truck className="w-3 h-3 mr-1" />Tomar
                                </Button>
                              )}
                              {entregasView === "en_proceso" && (
                                <>
                                  <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => openGPS(entrega)}>
                                    <Navigation className="w-3 h-3 mr-1" />GPS
                                  </Button>
                                  <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white h-8 text-xs" onClick={() => openConfirmDialog(entrega)}>
                                    <CheckCircle2 className="w-3 h-3 mr-1" />Confirmar
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
                {/* Individual Entregas */}
                {individualEntregas.map(entrega => (
                  <Card key={entrega.id} className="border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 text-sm">
                            {entrega.destino_nombre || entrega.cliente || "Sin nombre"}
                          </h3>
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{getDisplayAddress(entrega)}</span>
                          </p>
                          {entrega.destino_telefono && (
                            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              <a href={`tel:${entrega.destino_telefono}`} className="text-blue-600">
                                {entrega.destino_telefono}
                              </a>
                            </p>
                          )}
                          {/* Nombre de quien colocó la mensajería */}
                          {entrega.created_by_name && (
                            <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                              <User className="w-3 h-3 flex-shrink-0" />
                              <span>Solicitado por: <strong>{entrega.created_by_name}</strong></span>
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge 
                            variant="outline" 
                            className={
                              entregasView === "pendientes" ? "bg-yellow-100 text-yellow-700 border-yellow-300" :
                              entregasView === "en_proceso" ? "bg-blue-100 text-blue-700 border-blue-300" :
                              entregasView === "devueltas" ? "bg-orange-100 text-orange-700 border-orange-300" :
                              "bg-green-100 text-green-700 border-green-300"
                            }
                          >
                            {entregasView === "pendientes" ? "Pendiente" : entregasView === "en_proceso" ? "En Proceso" : entregasView === "devueltas" ? "Devuelta" : "Completada"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-slate-500"
                            onClick={() => openDetailsDialog(entrega)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Detalles
                          </Button>
                        </div>
                      </div>

                      {/* Show description preview if available */}
                      {entrega.descripcion && (
                        <p className="text-xs text-slate-600 mb-2 line-clamp-2 bg-slate-50 p-2 rounded">
                          {entrega.descripcion}
                        </p>
                      )}

                      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                        <Clock className="w-3 h-3" />
                        {entrega.created_at ? new Date(entrega.created_at).toLocaleDateString('es-DO', { 
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                        }) : 'Sin fecha'}
                        {entrega.tipo_documento_nombre && (
                          <>
                            <span className="mx-1">•</span>
                            <FileText className="w-3 h-3" />
                            {entrega.tipo_documento_nombre}
                          </>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {entregasView === "pendientes" && (
                          <Button 
                            size="sm" 
                            className="flex-1 bg-[#0C314C] hover:bg-[#2a4d7a] text-white h-9"
                            onClick={() => tomarEntrega(entrega.id)}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Tomar
                          </Button>
                        )}
                        
                        {entregasView === "en_proceso" && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1 h-9"
                              onClick={() => openGPS(entrega)}
                            >
                              <Navigation className="w-4 h-4 mr-1" />
                              GPS
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-9 w-9 p-0"
                              onClick={() => openChat(entrega)}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                            <Button 
                              data-testid={`btn-devuelto-${entrega.id}`}
                              size="sm" 
                              variant="outline"
                              className="h-9 border-orange-300 text-orange-600 hover:bg-orange-50"
                              onClick={() => openDevueltoDialog(entrega)}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white h-9"
                              onClick={() => openConfirmDialog(entrega)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Confirmar
                            </Button>
                          </>
                        )}

                        {entregasView === "completadas" && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full h-9"
                            onClick={() => openChat(entrega)}
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            Ver Chat
                          </Button>
                        )}

                        {entregasView === "devueltas" && (
                          <div className="w-full space-y-2">
                            {entrega.motivo_devolucion && (
                              <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs text-orange-800">
                                <span className="font-semibold">Motivo:</span> {entrega.motivo_devolucion}
                              </div>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full h-9"
                              onClick={() => openDetailsDialog(entrega)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Ver Detalles
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Chat Tab
          <div className="p-3">
            <h2 className="font-semibold text-slate-900 mb-3">Conversaciones</h2>
            {loadingChat ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No hay conversaciones</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <Card 
                    key={conv.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openChat(conv)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-12 h-12 bg-[#0C314C]">
                          <AvatarFallback className="bg-[#0C314C] text-white">
                            {getInitials(conv.destino_nombre || conv.cliente)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {conv.destino_nombre || conv.cliente || "Sin nombre"}
                          </h3>
                          <p className="text-xs text-slate-500">{conv.tipo_documento || "Entrega"}</p>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={
                            conv.estado === "completado" ? "bg-green-100 text-green-700" :
                            conv.estado === "en_proceso" ? "bg-blue-100 text-blue-700" :
                            "bg-yellow-100 text-yellow-700"
                          }
                        >
                          {conv.estado}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-slate-200 safe-area-bottom">
        <div className="grid grid-cols-2 h-14">
          <button
            data-testid="tab-entregas"
            onClick={() => setActiveTab('entregas')}
            className={`flex flex-col items-center justify-center gap-1 ${
              activeTab === 'entregas' ? 'text-[#0C314C]' : 'text-slate-400'
            }`}
          >
            <Package className={`w-5 h-5 ${activeTab === 'entregas' ? 'text-[#0C314C]' : ''}`} />
            <span className="text-xs font-medium">Entregas</span>
          </button>
          <button
            data-testid="tab-chat"
            onClick={() => setActiveTab('chat')}
            className={`flex flex-col items-center justify-center gap-1 ${
              activeTab === 'chat' ? 'text-[#0C314C]' : 'text-slate-400'
            }`}
          >
            <MessageCircle className={`w-5 h-5 ${activeTab === 'chat' ? 'text-[#0C314C]' : ''}`} />
            <span className="text-xs font-medium">Chat</span>
          </button>
        </div>
      </div>

      {/* Confirm Delivery Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] mx-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Confirmar Entrega
            </DialogTitle>
            <DialogDescription>
              Complete los datos para confirmar
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-auto">
            <div>
              <label className="text-sm font-medium text-slate-700">Nombre de quien recibe *</label>
              <Input
                value={confirmData.recipient_name}
                onChange={(e) => setConfirmData(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="Nombre completo"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Firma del destinatario *</label>
              <div className="mt-1 flex flex-col items-center">
                <SignaturePad 
                  ref={signatureRef} 
                  width={Math.min(280, window.innerWidth - 80)} 
                  height={120}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 text-xs"
                  onClick={() => signatureRef.current?.clear()}
                >
                  Limpiar firma
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Foto (opcional)</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={photoInputRef}
                onChange={handlePhotoCapture}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full mt-1"
                onClick={() => photoInputRef.current?.click()}
              >
                <Camera className="w-4 h-4 mr-2" />
                {confirmData.photo_data ? "Foto capturada ✓" : "Tomar foto"}
              </Button>
              {confirmData.photo_data && (
                <img 
                  src={confirmData.photo_data} 
                  alt="Preview" 
                  className="mt-2 w-full h-24 object-cover rounded-lg"
                />
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Notas (opcional)</label>
              <Textarea
                value={confirmData.notes}
                onChange={(e) => setConfirmData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Observaciones..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={confirmDelivery}
              disabled={confirming}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] mx-auto rounded-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-[#0C314C]" />
              Detalles de Entrega
            </DialogTitle>
          </DialogHeader>

          {selectedEntrega && (
            <div className="space-y-4">
              {/* Destinatario */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Destinatario
                </h4>
                <p className="text-sm text-slate-700">{selectedEntrega.destino_nombre || selectedEntrega.cliente || "No especificado"}</p>
                {selectedEntrega.destino_telefono && (
                  <a href={`tel:${selectedEntrega.destino_telefono}`} className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" />
                    {selectedEntrega.destino_telefono}
                  </a>
                )}
              </div>

              {/* Descripción del documento - PROMINENTE para mensajeros */}
              {(selectedEntrega.descripcion || selectedEntrega.documento_descripcion) && (
                <div className="bg-blue-50 border-2 border-blue-200 p-3 rounded-lg" data-testid="entrega-descripcion">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Descripción del Documento
                  </h4>
                  <p className="text-sm text-slate-800 font-medium whitespace-pre-wrap">{selectedEntrega.descripcion || selectedEntrega.documento_descripcion}</p>
                </div>
              )}

              {/* Direcciones */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Direcciones
                </h4>
                {selectedEntrega.direccion_envio && (
                  <div className="mb-2">
                    <p className="text-xs text-slate-500">Origen:</p>
                    <p className="text-sm text-slate-700">{selectedEntrega.direccion_envio}</p>
                  </div>
                )}
                {selectedEntrega.destino_direccion && (
                  <div>
                    <p className="text-xs text-slate-500">Destino:</p>
                    <p className="text-sm text-slate-700">{selectedEntrega.destino_direccion}</p>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 w-full"
                  onClick={() => openGPS(selectedEntrega)}
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Abrir en Google Maps
                </Button>
              </div>

              {/* Notas */}
              {selectedEntrega.notas && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Notas</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedEntrega.notas}</p>
                </div>
              )}

              {/* Info adicional */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2">Información</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Cliente</p>
                    <p className="text-slate-700">{selectedEntrega.cliente || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Solicitado por</p>
                    <p className="text-slate-700 font-medium text-purple-700">{selectedEntrega.created_by_name || selectedEntrega.user_name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Tipo</p>
                    <p className="text-slate-700">{selectedEntrega.tipo_documento_nombre || selectedEntrega.tipo || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Estado</p>
                    <p className="text-slate-700 capitalize">{selectedEntrega.estado || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Fecha</p>
                    <p className="text-slate-700">
                      {selectedEntrega.created_at 
                        ? new Date(selectedEntrega.created_at).toLocaleDateString('es-DO', {
                            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : "N/A"}
                    </p>
                  </div>
                  {selectedEntrega.es_urgente && (
                    <div>
                      <p className="text-xs text-slate-500">Prioridad</p>
                      <Badge className="bg-red-100 text-red-700">Urgente</Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Confirmación de entrega (firma y foto) */}
              {selectedEntrega.confirmacion_entrega && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Confirmación de Entrega
                  </h4>
                  
                  {/* Info */}
                  <div className="space-y-2 text-sm mb-3">
                    <div>
                      <span className="text-slate-500">Recibido por: </span>
                      <span className="font-medium text-slate-900">{selectedEntrega.confirmacion_entrega.recipient_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Mensajero: </span>
                      <span className="font-medium text-slate-900">{selectedEntrega.confirmacion_entrega.mensajero_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Fecha: </span>
                      <span className="text-slate-900">
                        {new Date(selectedEntrega.confirmacion_entrega.confirmed_at).toLocaleString('es-DO')}
                      </span>
                    </div>
                    {selectedEntrega.confirmacion_entrega.notes && (
                      <div>
                        <span className="text-slate-500">Notas: </span>
                        <span className="text-slate-900">{selectedEntrega.confirmacion_entrega.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Firma y Foto */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Firma */}
                    {selectedEntrega.confirmacion_entrega.signature_url && (
                      <div>
                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                          <PenLine className="w-3 h-3" />
                          Firma
                        </p>
                        <div 
                          className="bg-white rounded border border-slate-200 p-1 cursor-pointer"
                          onClick={() => window.open(`${API}${selectedEntrega.confirmacion_entrega.signature_url}`, '_blank')}
                        >
                          <img 
                            src={`${API}${selectedEntrega.confirmacion_entrega.signature_url}`}
                            alt="Firma"
                            className="max-h-20 mx-auto object-contain"
                          />
                        </div>
                      </div>
                    )}

                    {/* Foto */}
                    {selectedEntrega.confirmacion_entrega.photo_url && (
                      <div>
                        <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          Foto
                        </p>
                        <div 
                          className="bg-white rounded border border-slate-200 p-1 cursor-pointer"
                          onClick={() => window.open(`${API}${selectedEntrega.confirmacion_entrega.photo_url}`, '_blank')}
                        >
                          <img 
                            src={`${API}${selectedEntrega.confirmacion_entrega.photo_url}`}
                            alt="Foto de entrega"
                            className="max-h-20 mx-auto object-contain rounded"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Ubicación GPS */}
                  {selectedEntrega.confirmacion_entrega.latitude && selectedEntrega.confirmacion_entrega.longitude && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3 border-green-300 text-green-700"
                      onClick={() => {
                        const lat = selectedEntrega.confirmacion_entrega.latitude;
                        const lng = selectedEntrega.confirmacion_entrega.longitude;
                        window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                      }}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Ver ubicación de entrega
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)} className="w-full">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Devuelto Confirmation Dialog */}
      <Dialog open={showDevueltoDialog} onOpenChange={setShowDevueltoDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] mx-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              Marcar como Devuelta
            </DialogTitle>
            <DialogDescription>
              {selectedEntrega && (
                <span>
                  Entrega para <strong>{selectedEntrega.destino_nombre || selectedEntrega.cliente || "este destinatario"}</strong>. Indique el motivo de la devolución.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Motivo de devolución <span className="text-red-500">*</span></label>
              <Textarea
                data-testid="input-motivo-devolucion"
                value={motivoDevolucion}
                onChange={(e) => setMotivoDevolucion(e.target.value)}
                placeholder="Escriba el motivo por el cual se devuelve esta entrega..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDevueltoDialog(false)} className="flex-1" data-testid="btn-cancel-devuelto">
              Cancelar
            </Button>
            <Button 
              data-testid="btn-confirm-devuelto"
              onClick={marcarDevuelto}
              disabled={devueltoLoading || !motivoDevolucion.trim()}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {devueltoLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Confirmar Devolución
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
